import { Request } from "shared/src/services/Requests";
import { CustomerCartItemPrices } from "shared/src/types/CartPrice";
import { SessionStore } from "shared/src/uidispatcherapi/SessionStore";
const RequestBuilder = new Request.Builder("ordercapture", "customercart", true);

export type CartContext = {
    country: string;
    locale: string;
    storeId: string;
    customerId: string;
    cardholderNumber: string;
    fsdAddressId: string | null;
};

type GetCartRequest = {
    cartContext: CartContext;
    cartId: string;
};

type AddItemRequest = {
    requestId: string;
    cartContext: CartContext;
    cartId: string;
    bundleId: string;
    quantity: number;
};

type ItemBatch = {
    [bettyBundleId: string]: AddItemBatchItem;
};

export type AddItemBatchRequest = {
    cartContext: CartContext;
    cartId: string;
    items: ItemBatch;
    requestId: string;
    type: "BATCH";
};

type AddItemBatchItem = {
    bundleId: string;
    quantity: number;
    comment: string | undefined | null;
    sortingIndex: number;
};

type SetItemQuantityRequest = {
    requestId: string;
    cartContext: CartContext;
    cartId: string;
    bundleId: string;
    quantity: number;
};

export type CustomerCart = {
    cartId: string;
    cartVersion: number;
    items: {
        [bundleId: string]: CustomerCartItem;
    };
    eventHistory: Array<string>;
};

export type CustomerCartItem = {
    bundleId: string;
    quantity: number;
    prices: CustomerCartItemPrices | undefined | null;
    errors: CustomerCartItemError[];
};

export type CustomerCartItemError = {
    readonly reason: string;
};

export type CartEvent = {
    type: string;
    eventId: string;
    cartId: string;
    customerId: string;
    seqNum: number;
    payload: {
        bundleId: string | undefined | null;
        quantity: number | undefined | null;
    };
    status: {
        type: "Success" | "Failure";
    };
    prices:
        | {
              articlePrices: {
                  [x: string]: CustomerCartItemPrices;
              };
          }
        | undefined
        | null;
};

export type CartVersionResponse = {
    cartId: string;
    cartVersion: number;
    eventHistory: string[];
};

function getQueryParams(context: CartContext): {
    [x: string]: any;
} {
    return {
        country: context.country,
        locale: context.locale,
        fsdAddressId: context.fsdAddressId,
        storeId: context.storeId,
        customerId: context.customerId,
        cardholderNumber: context.cardholderNumber
    };
}

// this is different from the anon cart: here we get a whole object back
export function getCartVersion(params: GetCartRequest): Promise<CartVersionResponse | undefined | null> {
    const template = "/carts/{cartId}/version";
    const pathParams = { cartId: params.cartId };
    const queryParams = getQueryParams(params.cartContext);

    return RequestBuilder.path(template, pathParams)
        .query(queryParams)
        .get()
        .then((json) => json.data);
}

export function getCart(params: GetCartRequest): Promise<CustomerCart | undefined | null> {
    const template = "/carts/{cartId}";
    const pathParams = { cartId: params.cartId };
    const queryParams = getQueryParams(params.cartContext);

    return RequestBuilder.path(template, pathParams)
        .query(queryParams)
        .get()
        .then((json) => json.data);
}

export function addItem(params: AddItemRequest): Promise<void> {
    const template = "/carts/{cartId}/items";
    const pathParams = { cartId: params.cartId };
    const queryParams = getQueryParams(params.cartContext);
    const body = {
        requestId: params.requestId,
        bundleId: params.bundleId,
        quantity: params.quantity,
        customerId: params.cartContext.customerId
    };
    return RequestBuilder.path(template, pathParams).query(queryParams).body(body).post();
}

export function addItemBatch(params: AddItemBatchRequest): Promise<void> {
    const template = "/carts/{cartId}/items";
    const pathParams = { cartId: params.cartId };
    const queryParams = getQueryParams(params.cartContext);
    const body = {
        requestId: params.requestId,
        customerId: params.cartContext.customerId,
        items: params.items,
        type: params.type
    };
    return RequestBuilder.path(template, pathParams).query(queryParams).body(body).post();
}

export function setItemQuantity(params: SetItemQuantityRequest): Promise<void> {
    const template = "/carts/{cartId}/items/{bundleId}/quantity";
    const pathParams = { cartId: params.cartId, bundleId: params.bundleId };
    const queryParams = getQueryParams(params.cartContext);
    const body = {
        requestId: params.requestId,
        quantity: params.quantity,
        customerId: params.cartContext.customerId
    };
    return RequestBuilder.path(template, pathParams).query(queryParams).body(body).put();
}

export function createCartContext(): CartContext {
    return {
        country: SessionStore.getCountry(),
        locale: SessionStore.getLocale(),
        storeId: SessionStore.getSelectedStoreId(),
        customerId: SessionStore.getCustomerId(),
        cardholderNumber: SessionStore.getCardholderNumber() || "",
        fsdAddressId: SessionStore.getSelectedDeliveryAddressId()
    };
}
